import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { Target, History, Save, Plus, Trash2, Calendar, Lightbulb } from 'lucide-react';
import WheelRadarChart from './WheelRadarChart';
import WheelScoreEditor from './WheelScoreEditor';
import WheelHistory from './WheelHistory';
import WheelInsights from './WheelInsights';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Default scores for all 8 categories
const DEFAULT_SCORES = [
  { category: 'Career', score: 5, notes: '', whatsWorking: '', challenges: '', ideal: '' },
  { category: 'Financial', score: 5, notes: '', whatsWorking: '', challenges: '', ideal: '' },
  { category: 'Health', score: 5, notes: '', whatsWorking: '', challenges: '', ideal: '' },
  { category: 'Relationships', score: 5, notes: '', whatsWorking: '', challenges: '', ideal: '' },
  { category: 'Personal Growth', score: 5, notes: '', whatsWorking: '', challenges: '', ideal: '' },
  { category: 'Fun & Recreation', score: 5, notes: '', whatsWorking: '', challenges: '', ideal: '' },
  { category: 'Physical Environment', score: 5, notes: '', whatsWorking: '', challenges: '', ideal: '' },
  { category: 'Family & Friends', score: 5, notes: '', whatsWorking: '', challenges: '', ideal: '' }
];

function WheelOfLife({ clientId, isCoach = false, onScoresChange, onAssessmentChange }) {
  const [assessments, setAssessments] = useState([]);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [currentScores, setCurrentScores] = useState(DEFAULT_SCORES);
  const [selectedView, setSelectedView] = useState('current'); // 'current', 'history', or 'insights'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [comparisonAssessment, setComparisonAssessment] = useState(null);
  const [assessmentTitle, setAssessmentTitle] = useState('');
  const [assessmentNotes, setAssmentNotes] = useState('');

  useEffect(() => {
    if (clientId) {
      fetchAssessments();
    }
  }, [clientId]);

  // Notify parent when scores change
  useEffect(() => {
    if (onScoresChange) {
      onScoresChange(currentScores);
    }
  }, [currentScores, onScoresChange]);

  // Notify parent when assessment changes
  useEffect(() => {
    if (onAssessmentChange) {
      onAssessmentChange(currentAssessment?.id || null);
    }
  }, [currentAssessment, onAssessmentChange]);

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/clients/${clientId}/wheel-assessments`);
      const assessmentsData = response.data;
      setAssessments(assessmentsData);

      // Load most recent assessment or create new one
      if (assessmentsData.length > 0) {
        await loadAssessment(assessmentsData[0].id);
      } else {
        // No assessments yet, start with defaults
        setCurrentScores(DEFAULT_SCORES);
        setCurrentAssessment(null);
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Error fetching wheel assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAssessment = async (assessmentId) => {
    try {
      const response = await axios.get(`${API_URL}/wheel-assessments/${assessmentId}`);
      const assessment = response.data;
      setCurrentAssessment(assessment);
      setCurrentScores(assessment.scores || DEFAULT_SCORES);
      setAssessmentTitle(assessment.title || '');
      setAssmentNotes(assessment.notes || '');
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error loading assessment:', error);
    }
  };

  const handleScoresChange = (newScores) => {
    setCurrentScores(newScores);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (currentAssessment) {
        // Update existing assessment
        await axios.put(`${API_URL}/wheel-assessments/${currentAssessment.id}`, {
          title: assessmentTitle || 'Wheel of Life Assessment',
          notes: assessmentNotes
        });

        // Update scores
        await axios.put(`${API_URL}/wheel-assessments/${currentAssessment.id}/scores`, {
          scores: currentScores
        });

        toast.success('Assessment updated successfully!');
      } else {
        // Create new assessment
        const assessmentResponse = await axios.post(`${API_URL}/clients/${clientId}/wheel-assessments`, {
          title: assessmentTitle || 'Wheel of Life Assessment',
          notes: assessmentNotes
        });

        const newAssessment = assessmentResponse.data;

        // Save scores
        await axios.put(`${API_URL}/wheel-assessments/${newAssessment.id}/scores`, {
          scores: currentScores
        });

        setCurrentAssessment(newAssessment);
        toast.success('Assessment created successfully!');
      }

      setHasUnsavedChanges(false);
      await fetchAssessments(); // Refresh list
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Failed to save assessment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNew = async () => {
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm('You have unsaved changes. Do you want to discard them and create a new assessment?');
      if (!confirmDiscard) return;
    }

    setCurrentAssessment(null);
    setCurrentScores(DEFAULT_SCORES);
    setAssessmentTitle('');
    setAssmentNotes('');
    setHasUnsavedChanges(false);
    setSelectedView('current');
  };

  const handleDelete = async () => {
    if (!currentAssessment) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this assessment? This cannot be undone.');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_URL}/wheel-assessments/${currentAssessment.id}`);
      toast.success('Assessment deleted successfully!');
      await fetchAssessments();

      // Load most recent or reset
      if (assessments.length > 1) {
        const nextAssessment = assessments.find(a => a.id !== currentAssessment.id);
        if (nextAssessment) {
          await loadAssessment(nextAssessment.id);
        }
      } else {
        setCurrentAssessment(null);
        setCurrentScores(DEFAULT_SCORES);
        setAssessmentTitle('');
        setAssmentNotes('');
      }
    } catch (error) {
      console.error('Error deleting assessment:', error);
      toast.error('Failed to delete assessment. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 mx-auto mb-4" style={{ borderColor: 'var(--accent-30)', borderTopColor: 'var(--accent)' }}></div>
          <p className="text-[var(--text-tertiary)]">Loading RMMBR Circle...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with tabs */}
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Target className="w-6 h-6" />
              RMMBR Circle
            </h2>
            <p className="text-sm text-[var(--text-tertiary)] mt-1">
              Assess and balance different areas of your life
            </p>
          </div>

          <div className="flex items-center gap-2">
            {currentAssessment && (
              <button
                onClick={handleDelete}
                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                title="Delete assessment"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={handleCreateNew}
              className="bg-[var(--accent)]/20 hover:bg-[var(--accent)]/30 text-[var(--accent)] px-4 py-2 rounded-lg text-sm transition-all border border-[var(--accent)]/30 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Assessment
            </button>
            <button
              onClick={handleSave}
              disabled={!hasUnsavedChanges || saving}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-[var(--text-primary)] px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-0">
          <button
            onClick={() => setSelectedView('current')}
            className={`px-4 py-2 text-sm font-medium transition-all relative ${
              selectedView === 'current'
                ? 'text-[var(--accent)]'
                : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'
            }`}
          >
            <Target className="w-4 h-4 inline mr-2" />
            Current Assessment
            {selectedView === 'current' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent)]"></div>
            )}
          </button>
{isCoach && (
            <button
              onClick={() => setSelectedView('insights')}
              className={`px-4 py-2 text-sm font-medium transition-all relative ${
                selectedView === 'insights'
                  ? 'text-[var(--accent)]'
                  : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'
              }`}
            >
              <Lightbulb className="w-4 h-4 inline mr-2" />
              AI Insights
              {selectedView === 'insights' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent)]"></div>
              )}
            </button>
          )}
          <button
            onClick={() => setSelectedView('history')}
            className={`px-4 py-2 text-sm font-medium transition-all relative ${
              selectedView === 'history'
                ? 'text-[var(--accent)]'
                : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'
            }`}
          >
            <History className="w-4 h-4 inline mr-2" />
            History ({assessments.length})
            {selectedView === 'history' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent)]"></div>
            )}
          </button>
        </div>
      </div>

      {/* Current Assessment View */}
      {selectedView === 'current' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left: Visualization */}
          <div className="space-y-4">
            <WheelRadarChart
              scores={currentScores}
              comparisonScores={comparisonAssessment?.scores}
              showComparison={!!comparisonAssessment}
            />

            {/* Assessment metadata */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-4 space-y-3">
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">Assessment Title</label>
                <input
                  type="text"
                  value={assessmentTitle}
                  onChange={(e) => {
                    setAssessmentTitle(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="e.g., Q1 2025 Review"
                  className="w-full bg-white/10 text-[var(--text-primary)] px-3 py-2 rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">Notes (Optional)</label>
                <textarea
                  value={assessmentNotes}
                  onChange={(e) => {
                    setAssmentNotes(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="Any context or reflections about this assessment..."
                  className="w-full bg-white/10 text-[var(--text-primary)] px-3 py-2 rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] resize-none text-sm"
                  rows="3"
                />
              </div>
              {currentAssessment && (
                <div className="flex items-center gap-2 text-xs text-[var(--text-tertiary)] pt-2 border-t border-white/10">
                  <Calendar className="w-3 h-3" />
                  <span>Created: {new Date(currentAssessment.created_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Score Editor */}
          <div>
            <WheelScoreEditor
              scores={currentScores}
              onScoresChange={handleScoresChange}
            />
          </div>
        </div>
      )}

      {/* AI Insights View - Coach Only */}
      {isCoach && selectedView === 'insights' && (
        <WheelInsights
          assessmentId={currentAssessment?.id}
          currentScores={currentScores}
        />
      )}

      {/* History View */}
      {selectedView === 'history' && (
        <WheelHistory
          assessments={assessments}
          currentAssessment={currentAssessment}
          onSelectAssessment={(assessment) => {
            setSelectedView('current');
            loadAssessment(assessment.id);
          }}
          onCompare={setComparisonAssessment}
        />
      )}

      {/* Unsaved changes warning */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-6 right-6 bg-[var(--accent)] text-[var(--text-primary)] px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          <span className="text-sm font-medium">You have unsaved changes</span>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-sm font-medium transition-colors"
          >
            {saving ? 'Saving...' : 'Save Now'}
          </button>
        </div>
      )}
    </div>
  );
}

export default WheelOfLife;
